import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { GraduationCap, Award, TrendingUp, TrendingDown } from 'lucide-react';
import {
  BarChart, Bar, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList
} from 'recharts';

export default function MapaDisciplina({ year, trimestre, disciplinas, initialDisciplinaId }) {
  const [loading, setLoading] = useState(false);
  const [selectedDisciplina, setSelectedDisciplina] = useState(initialDisciplinaId || '');
  const [data, setData] = useState(null);
  
  useEffect(() => {
    if (initialDisciplinaId && !selectedDisciplina) setSelectedDisciplina(initialDisciplinaId);
    else if (!selectedDisciplina && disciplinas.length > 0) setSelectedDisciplina(disciplinas[0].id);
  }, [initialDisciplinaId, disciplinas]);
  
  useEffect(() => {
    if (selectedDisciplina) {
      loadData();
    }
  }, [selectedDisciplina, year, trimestre]);
  
  const loadData = async () => {
    setLoading(true);
    try {
      const params = { year };
      if (trimestre) params.trimestre = trimestre;
      
      const res = await api.get(`/central-notas/mapa-disciplina/${selectedDisciplina}`, { params });
      setData(res.data);
    } catch (error) {
      console.error('Erro ao carregar mapa:', error);
      toast.error('Erro ao carregar dados da disciplina');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="space-y-6" data-testid="mapa-disciplina">
      {/* Seletor */}
      <Card className="bg-white border border-purple-200/50 shadow-sm">
        <CardContent className="py-4">
          <div className="flex items-center gap-4">
            <div className="p-2 rounded-lg bg-green-100">
              <GraduationCap className="w-5 h-5 text-green-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-slate-500">Selecione uma disciplina para análise</p>
            </div>
            <Select value={selectedDisciplina} onValueChange={setSelectedDisciplina}>
              <SelectTrigger className="w-[300px] bg-slate-50 border-slate-200">
                <SelectValue placeholder="Selecione a disciplina..." />
              </SelectTrigger>
              <SelectContent className="bg-white max-h-[300px]">
                {disciplinas.map((d) => (
                  <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      
      {!selectedDisciplina ? (
        <Card className="bg-white border border-purple-200/50 shadow-sm">
          <CardContent className="py-16 text-center">
            <GraduationCap className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">Selecione uma Disciplina</h3>
            <p className="text-slate-500">Escolha uma disciplina acima para ver a análise detalhada</p>
          </CardContent>
        </Card>
      ) : loading ? (
        <div className="space-y-4">
          <Skeleton className="h-32 bg-slate-100" />
          <div className="grid grid-cols-2 gap-4">
            <Skeleton className="h-64 bg-slate-100" />
            <Skeleton className="h-64 bg-slate-100" />
          </div>
        </div>
      ) : data && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
              <CardContent className="py-4">
                <p className="text-green-100 text-sm">Média Geral</p>
                <p className="text-3xl font-bold">{data.disciplina?.media_geral?.toFixed(1)}</p>
              </CardContent>
            </Card>
            <Card className="bg-white border border-slate-200">
              <CardContent className="py-4">
                <p className="text-slate-500 text-sm">Total de Turmas</p>
                <p className="text-3xl font-bold text-slate-800">{data.disciplina?.total_turmas}</p>
              </CardContent>
            </Card>
            <Card className="bg-white border border-slate-200">
              <CardContent className="py-4">
                <p className="text-slate-500 text-sm">Total de Alunos</p>
                <p className="text-3xl font-bold text-slate-800">{data.disciplina?.total_alunos}</p>
              </CardContent>
            </Card>
            <Card className="bg-white border border-slate-200">
              <CardContent className="py-4">
                <p className="text-slate-500 text-sm">Disciplina</p>
                <p className="text-lg font-bold text-slate-800 truncate">{data.disciplina?.nome}</p>
              </CardContent>
            </Card>
          </div>
          
          {/* Gráficos */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Média por Turma */}
            <Card className="bg-white border border-purple-200/50 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-slate-800 text-base">Média por Turma</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.medias_turma} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis type="number" domain={[0, 10]} stroke="#94A3B8" />
                      <YAxis type="category" dataKey="turma" stroke="#94A3B8" width={120} tick={{ fontSize: 10 }} />
                      <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #E2E8F0', borderRadius: '8px' }} />
                      <Bar dataKey="media" radius={[0, 4, 4, 0]}>
                        {data.medias_turma?.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            
            {/* Média por Tipo */}
            <Card className="bg-white border border-purple-200/50 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-slate-800 text-base">Média por Tipo de Avaliação</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.medias_tipo}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="tipo" stroke="#94A3B8" tick={{ fontSize: 10 }} />
                      <YAxis domain={[0, 10]} stroke="#94A3B8" />
                      <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #E2E8F0', borderRadius: '8px' }} />
                      <Bar dataKey="media" fill="#8E52AB" radius={[4, 4, 0, 0]}>
                        <LabelList dataKey="media" position="top"
                                   formatter={(v) => (v === null || v === undefined || v === '' || Number.isNaN(Number(v)) ? null : Number(v).toFixed(1).replace('.', ','))}
                                   style={{ fontSize: 12, fontWeight: 700, fill: '#3F1F5C' }} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Top e Bottom Alunos */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Alunos */}
            <Card className="bg-white border border-green-200 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-slate-800 text-base flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                  Melhores Desempenhos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {data.top_alunos?.map((aluno, idx) => (
                    <div key={aluno.student_id} className="flex items-center gap-3 p-2 rounded-lg bg-green-50">
                      <span className="w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center font-bold">
                        {idx + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{aluno.nome}</p>
                        <p className="text-xs text-slate-500">{aluno.turma}</p>
                      </div>
                      <span className="font-bold text-green-600">{aluno.media?.toFixed(1)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            {/* Bottom Alunos */}
            <Card className="bg-white border border-red-200 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-slate-800 text-base flex items-center gap-2">
                  <TrendingDown className="w-5 h-5 text-red-500" />
                  Precisam de Atenção
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {data.bottom_alunos?.map((aluno, idx) => (
                    <div key={aluno.student_id} className="flex items-center gap-3 p-2 rounded-lg bg-red-50">
                      <span className="w-6 h-6 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-bold">
                        {idx + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{aluno.nome}</p>
                        <p className="text-xs text-slate-500">{aluno.turma}</p>
                      </div>
                      <span className="font-bold text-red-600">{aluno.media?.toFixed(1)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
